import { BodyLarge, Button, colors, Small } from '@casavo/base-ui'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import React, { MutableRefObject, SyntheticEvent, useCallback, useEffect, useState } from 'react'

import ButtonCircleIcon from '@/components/ButtonCircleIcon'
import Chevron from '@/components/Icon/Chevron'
import Location from '@/components/Icon/Location'
import Phone from '@/components/Icon/Phone'
import Tap from '@/components/Icon/Tap'
import ListingCard from '@/components/ListingCard'
import ListingCardMapMobile from '@/components/ListingCardMapMobile'
import { PoiFilterButton } from '@/components/poi/PoiFilterButton'
import PoiCard from '@/components/PoiCard'
import { RequestContactCTA } from '@/components/RequestContactCTA'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { ModalContextType } from '@/context/ModalContext'
import { ButtonLink } from '@/design-lib/components/ButtonLink'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { flexCenter, gap } from '@/shared-styles/styles.css'
import { MapPoi, Poi, PoiCategory, PoiType } from '@/types/poi'
import getTranslatedTitle from '@/utils/get-translated-title'
import { isThirdParty } from '@/utils/listing/listingType'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { poiTypes } from '@/utils/points-of-interest'
import { handleListingDetailMapClick } from '@/utils/tracking/eventsHandlers'

import ModalHeaderTitle from '../ModalHeaderTitle'
import { detailModalContent, mapModalListingCard, poiCardWrapperMobile, poiFilters, spacer } from '../style.css'

import { detailModalHeaderFullScreen, mapModalInner } from './styles.css'

export type MarkerClicked = null | MapPoi | 'property'

const filterOrder: PoiCategory[] = ['TRANSPORT', 'SHOP', 'SCHOOL']

const SimpleGoogleMapsNoSsr = dynamic(() => import('../../../components/SimpleGoogleMaps'), {
  ssr: false,
})

type Props = {
  cities: ContextValue
  closeModal: ModalContextType['closeModal']
  closestPointsOfInterest: MapPoi[]
  filter?: PoiCategory | 'CLOSEST'
  handleOpenDetailFormModal: (e: SyntheticEvent) => void
  handleTrackingCallAgency: (eventCategory: string) => void
  listing: ListingCate
  pointsOfInterest: MapPoi[]
  selectedMarker?: MarkerClicked
  similarPropertiesRef: MutableRefObject<HTMLDivElement>
}

export const ModalMap: React.FC<Props> = ({
  cities,
  closeModal,
  closestPointsOfInterest,
  filter = 'CLOSEST',
  handleOpenDetailFormModal,
  handleTrackingCallAgency,
  listing,
  pointsOfInterest,
  selectedMarker = null,
  similarPropertiesRef,
}) => {
  const device = useDevice()
  const {
    coordinates,
    detail: { city, titles },
  } = listing
  const [cardOpen, setCardOpen] = useState<MarkerClicked>(selectedMarker)
  const [visiblePois, setVisiblePois] = useState(closestPointsOfInterest)
  const [currentFilter, setCurrentFilter] = useState<PoiCategory | 'CLOSEST'>(filter)

  const { lang, t } = useTranslation()
  const listingCity = cities.list.find((item) => item.slug === city)

  const handleClickOnCard = (prevCardOpen: MarkerClicked, clickedCard: MarkerClicked) => {
    const newCard = prevCardOpen === clickedCard ? null : clickedCard
    newCard !== null && handleListingDetailMapClick(newCard === 'property' ? 'PropertyMarker' : `Poi${newCard.poiType}`)
    setCardOpen(newCard)
  }

  const onMapClicked = useCallback(() => {
    handleClickOnCard(cardOpen, null)
  }, [handleClickOnCard])

  const handlePoiFilter = (clickedFilter: PoiCategory) => {
    setCardOpen(null)
    if (currentFilter === clickedFilter) setCurrentFilter('CLOSEST')
    else {
      setCurrentFilter(clickedFilter)
      handleListingDetailMapClick(`PoiFilter${clickedFilter}`)
    }
  }

  useEffect(() => {
    if (currentFilter === 'CLOSEST') setVisiblePois(closestPointsOfInterest)
    else
      setVisiblePois(
        pointsOfInterest.filter((p) => {
          return poiTypes[currentFilter].types.includes(p.poiType as PoiType)
        })
      )
  }, [currentFilter])

  // Show POIs only for main cities: eg. milano, roma, firenze, etc...
  const shouldShowPoi = cities.router.area.name === cities.router.city?.name && isLocaleEnabledFor('POIs', lang)

  return (
    <>
      <div className={cn(detailModalContent, sprinkles({ overflow: 'hidden' }))}>
        <div className={detailModalHeaderFullScreen}>
          {device === 'desktop' ? (
            <>
              <ModalHeaderTitle
                cityName={listingCity.translatedCityName}
                closeModal={closeModal}
                title={getTranslatedTitle(titles, lang)}
              />
              <div className={sprinkles({ alignItems: 'center', display: 'flex' })}>
                <Location className={sprinkles({ marginRight: 's' })} />
                <BodyLarge>{t('common:text-map')}</BodyLarge>
              </div>
            </>
          ) : (
            <ButtonCircleIcon ariaLabel="close dialog" onClick={() => closeModal()}>
              <Chevron direction="left" />
            </ButtonCircleIcon>
          )}
        </div>

        {shouldShowPoi && (
          <div className={mapModalInner} data-testid="map-pois-filters">
            {device === 'desktop' && (
              <Small strong className={sprinkles({ textTransform: 'uppercase' })}>
                {t(`listing-detail:poi_discover`)}
              </Small>
            )}
            <div className={poiFilters}>
              {filterOrder.map((p) => (
                <PoiFilterButton
                  key={p}
                  handlePoiFilter={handlePoiFilter}
                  isActive={currentFilter === p}
                  label={t(`listing-detail:${p}.poi_category`)}
                  poiCategory={p}
                />
              ))}
              <div className={spacer} />
            </div>
          </div>
        )}
        <div className={sprinkles({ height: '100%', paddingY: { tablet: 'l' } })}>
          <SimpleGoogleMapsNoSsr
            coordinates={coordinates}
            currentFilter={currentFilter}
            enableTouch={device === 'mobile'}
            isInModal={true}
            markerChild={
              <AnimatePresence>
                {device === 'desktop' && cardOpen === 'property' && (
                  <motion.div
                    animate={{ opacity: 1, x: -215, y: -180 }}
                    exit={{ opacity: 0, x: -215, y: -170 }}
                    initial={{ opacity: 0, x: -215, y: -170 }}
                    style={{ width: 470 }}
                    transition={{ duration: 0.3, type: 'tween' }}
                  >
                    <ListingCard isLoading={false} isOnMap={true} listing={listing} onListingCardHover={null} />
                  </motion.div>
                )}
              </AnimatePresence>
            }
            markerClicked={cardOpen}
            options={{
              disableDoubleClickZoom: cardOpen !== null,
              zoomControl: device === 'desktop',
            }}
            pointsOfInterest={visiblePois}
            zoom={16}
            onClick={onMapClicked}
            onClickOnMarker={() => {}}
            onClickPOI={(poi) => handleClickOnCard(cardOpen, poi)}
          />
        </div>
        {device === 'mobile' && (
          <>
            <ListingCardMobileDetail
              handleOpenDetailFormModal={handleOpenDetailFormModal}
              handleTrackingCallAgency={handleTrackingCallAgency}
              property={listing}
              similarPropertiesRef={similarPropertiesRef}
            />
            {cardOpen && (
              <div className={poiCardWrapperMobile}>
                <PoiCard poi={cardOpen as Poi} />
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

type PropsListingCardMobileDetail = {
  handleOpenDetailFormModal: (e: SyntheticEvent) => void
  handleTrackingCallAgency: (eventCategory: string) => void
  property: ListingCate
  similarPropertiesRef: MutableRefObject<HTMLDivElement>
}

export const ListingCardMobileDetail: React.FC<PropsListingCardMobileDetail> = ({
  handleOpenDetailFormModal,
  handleTrackingCallAgency,
  property,
  similarPropertiesRef,
}) => {
  const { lang, t } = useTranslation()

  /** Display the call button only for third party properties */
  const shouldShowCallCta = isThirdParty(property)

  /** Exclude unsupported locales and third party properties from the visit experience */
  const canHaveVisitFunctionality = !isThirdParty(property)
  const listingTitle = property.detail.titles.find((title) => title.language === lang).text!
  return (
    <div className={mapModalListingCard}>
      <ListingCardMapMobile isAggregateListOpen={false} isOnDetail={true} property={property} />
      <div data-testid="map-contact-buttons-wrapper">
        {shouldShowCallCta && (
          <ButtonLink
            center
            fullWidth
            className={sprinkles({ flex: 1 })}
            href={`tel:${property.contactInfo.telephoneNumberContact}`}
            Icon={(props) => <Phone {...props} color={colors.greyscale600} height={20} width={20} />}
            label={t('listing-detail:phone_call')}
            sizeVariant="small"
            styleVariant="secondary"
            onClick={() => {
              handleTrackingCallAgency('ListingDetailMap')
            }}
          />
        )}

        {canHaveVisitFunctionality ? (
          <div className={[flexCenter, gap.s, sprinkles({ marginTop: 'l' })].join(' ')}>
            <RequestContactCTA
              isMobile
              listing={property}
              listingTitle={listingTitle}
              requestType="info"
              similarPropertiesRef={similarPropertiesRef}
            />
            <RequestContactCTA
              isMobile
              data-testid="request-visit-button"
              listing={property}
              listingTitle={listingTitle}
              requestType="visit"
              similarPropertiesRef={similarPropertiesRef}
            />
          </div>
        ) : (
          <Button
            center
            fullWidth
            className={sprinkles({ flex: 1 })}
            Icon={(props) => <Tap {...props} />}
            label={t('listing-detail:contact_us')}
            sizeVariant="small"
            onClick={handleOpenDetailFormModal}
          />
        )}
      </div>
    </div>
  )
}
